import React from 'react';
import { connect } from 'react-redux';
import { EmailIntegrations } from 'common-entities';

import DesignerTestTab from './email-designer-test-tab';
import DesignerContentTab from './email-designer-content-tab';
import DesignerSettingsTab from './email-designer-settings-tab';

import DesignerHelpSection from './email-designer-help';

export const EMAIL_INTEGRATIONS_FORM_ID = 'email-integrations-form';

import {
  BaseToolbar,
  Button,
  CKForm,
  Header,
  IsInvertedProvider,
  Menu,
  Message,
  Modal,
  PromptModal,
  PropTypes,
  TabMenu,
  createComponent,
  enums,
} from 'console-kit';

const EmailDesigner = createComponent({
  displayName: 'EmailDesigner',
  propTypes:   {
    showCancelChangesWarningMessage: PropTypes.bool,
    emailTemplate:                   PropTypes.object,
    isMessageDisplayed:              PropTypes.bool,
    isModalVisible:                  PropTypes.bool,
    tabIndex:                        PropTypes.string,
    previewEmail:                    PropTypes.string,
    token:                           PropTypes.object,
    isSendingTestEmail:              PropTypes.bool,

    closeTokenMessage:             PropTypes.func.isRequired,
    tryCloseEmailTemplateDesigner: PropTypes.func.isRequired,
    updateEmailTemplateField:      PropTypes.func.isRequired,
    updateEmailTemplate:           PropTypes.func.isRequired,
    changeDesignerTab:             PropTypes.func.isRequired,
    hideDesignerMessage:           PropTypes.func.isRequired,
    closeEmailTemplateDesigner:    PropTypes.func.isRequired,
    setPreviewEmail:               PropTypes.func.isRequired,
    checkEmailTemplateTokens:      PropTypes.func.isRequired,
    sendPreviewEmailTemplate:      PropTypes.func.isRequired,
  },
  defaultProps: {
    showCancelChangesWarningMessage: false,
    isMessageDisplayed:              false,
    isModalVisible:                  false,
    previewEmail:                    '',
    isSendingTestEmail:              false,
    tabIndex:                        'SETTINGS',
    token:                           {},
    emailTemplate:                   {
      id:             0,
      name:           '',
      instanceConfig: {
        from:             '',
        //these 5 are for internal use ONLY, they will merge and form the 'from' property
        _fromDomain:      '',
        _fromAddress:     '',
        _fromFriendly:    '',
        _replyTo:         '',
        _replyToFriendly: '',
        to:               '',
        replyTo:          '',
        subject:          '',
        type:             '',
      },
    },
  },
  lifecycles: {
    shouldComponentUpdate(nextProps) {
      return !_.isEqual(this.props, nextProps);
    },
  },
  handlers: {
    _updateEmailTemplate: (props) => () => {
      props.updateEmailTemplate(props.emailTemplate);
    },

    _checkEmailTemplateTokens: (props) => () => {
      props.checkEmailTemplateTokens(props.emailTemplate.instanceConfig);
    },

    _fieldChanged: (props) => (emailTemplate) => {
      props.updateEmailTemplateField(emailTemplate);
    },
  },
  render(props) {
    const _validation = (
      <Message
        isGrowler
        isDisplayed={props.showValidationMessage}
        messageType={enums.messageTypes.WARNING}
        onClose={props.closeValidatationMessage}
      >
        <div>
          <Header
            text="invalid fields"
            byline="Please update the following invalid fields before saving:"
            color={enums.colors.WHITE}
            size={enums.sizes.X7}
          />
          <For each="e" of={props.formErrors} index="i">
            <Header key={i} byline={e} color={enums.colors.WHITE} size={enums.sizes.X7} />
          </For>
        </div>
      </Message>
    );

    const _settings = (
      <DesignerSettingsTab
        id={props.emailTemplate.id}
        name={props.emailTemplate.name}
        from={props.emailTemplate.instanceConfig.from}
        _fromDomain={props.emailTemplate.instanceConfig._fromDomain}
        _fromAddress={props.emailTemplate.instanceConfig._fromAddress}
        _fromFriendly={props.emailTemplate.instanceConfig._fromFriendly}
        _replyToFriendly={props.emailTemplate.instanceConfig._replyToFriendly}
        _replyTo={props.emailTemplate.instanceConfig._replyTo}
        to={props.emailTemplate.instanceConfig.to}
        replyTo={props.emailTemplate.instanceConfig.replyTo}
        subject={props.emailTemplate.instanceConfig.subject}
        type={props.emailTemplate.instanceConfig.type}
        checkEmailTemplateTokens={props._checkEmailTemplateTokens}
        fieldChanged={props._fieldChanged}
      />
    );

    const _content = (
      <DesignerContentTab
        body={props.emailTemplate.instanceConfig.body}
        id={props.emailTemplate.id}
        fieldChanged={props._fieldChanged}
        checkEmailTemplateTokens={props._checkEmailTemplateTokens}
      />
    );

    const _preview = (
      <DesignerTestTab
        id={props.emailTemplate.id}
        from={props.emailTemplate.instanceConfig.from}
        emailTemplate={props.emailTemplate}
        token={props.token}
        previewEmail={props.previewEmail}
        isSendingTestEmail={props.isSendingTestEmail}
        checkEmailTemplateTokens={props._checkEmailTemplateTokens}
        sendPreviewEmailTemplate={props.sendPreviewEmailTemplate}
        setPreviewEmail={props.setPreviewEmail}
      />
    );

    const _info = <DesignerHelpSection token={props.token} />;

    const _warningMessage = (
      <IsInvertedProvider isInverted={true}>
        <PromptModal
          header="Unsaved changes"
          byline="Are you sure you want to discard your changes?"
          promptType={enums.promptTypes.WARNING}
          isOpen={props.isMessageDisplayed}
          acknowledgeAction={{
            onClick: props._updateEmailTemplate,
            text:    'Save & close',
          }}
          cancelAction={{
            onClick: props.closeEmailTemplateDesigner,
            text:    'Discard changes',
          }}
        />
      </IsInvertedProvider>
    );

    const _tokenWarningMessage = (
      <Message
        onClose={props.closeTokenMessage}
        messageType={enums.messageTypes.WARNING}
        isDisplayed={props.showTokenWarning}
      >
        <Header color={enums.colors.WHITE} text="Unknown or invalid tokens" />
        <If condition={props.token.required.length > 0}>
          <Header byline={`Missing required tokens: ${props.token.required.join(', ')}`} color={enums.colors.WHITE} />
        </If>
        <If condition={props.token.unknown.length > 0}>
          <Header byline={`Unknown tokens: ${props.token.unknown.join(', ')}`} color={enums.colors.WHITE} />
        </If>
        <Header
          byline="A list of valid tokens is available in the Help panel on the right, which includes those derived from your chosen form."
          color={enums.colors.WHITE}
        />
      </Message>
    );

    const _testEmailMessage = (
      <Message
        onClose={props.closeTestEmailMessage}
        messageType={enums.messageTypes.SUCCESS}
        isDisplayed={props.showTestEmailMessage}
        isGrowler
        autoClose
      >
        <Header
          color={enums.colors.WHITE}
          text="Test message sent"
          byline="Your test email was successfully sent. Please check your inbox."
        />
      </Message>
    );

    return (
      <If condition={props.isModalVisible}>
        <Modal
          hasCloseIcon={false}
          isFullscreen
          isSolid
          isInverted
          closeOnEscape={false}
          showModal={props.isModalVisible}
        >
          <div className="split-window">
            <CKForm.Form formId={EMAIL_INTEGRATIONS_FORM_ID}>
              {_validation}
              {_warningMessage}
              {_testEmailMessage}

              <div className="split-window__inner">
                <div className="split-window__west split-window--flex-width">
                  <div className="split-window-west__toolbar">
                    <TabMenu activeValue={props.tabIndex} onItemClick={props.changeDesignerTab}>
                      <Menu.Item name="Settings" value="SETTINGS" />
                      <Menu.Item name="Content" value="CONTENT" />
                      <Menu.Item name="Test" value="TEST" />
                    </TabMenu>
                  </div>
                  <div className="split-window-west__panel">
                    {_tokenWarningMessage}
                    <If condition={props.tabIndex === 'SETTINGS'}>{_settings}</If>
                    <If condition={props.tabIndex === 'CONTENT'}>{_content}</If>
                    <If condition={props.tabIndex === 'TEST'}>{_preview}</If>
                  </div>
                </div>
                <div className="split-window__east split-window--fix-width-small">
                  <div className="split-window-east__toolbar">
                    <IsInvertedProvider isInverted={false}>
                      <BaseToolbar.Editor
                        header={<Header text="Email Designer" />}
                        textAlign={enums.textAlignments.LEFT}
                        actions={
                          <Button.Group>
                            <Button.Secondary text="Cancel" onClick={props.tryCloseEmailTemplateDesigner} />
                            <Button.Primary
                              text="Save"
                              onClick={props._updateEmailTemplate}
                              color={enums.colors.DARKER}
                            />
                          </Button.Group>
                        }
                      />
                    </IsInvertedProvider>
                  </div>
                  <div className="split-window-east__panel split-window-panel--align-top">
                    <IsInvertedProvider isInverted={false}>{_info}</IsInvertedProvider>
                  </div>
                </div>
              </div>
            </CKForm.Form>
          </div>
        </Modal>
      </If>
    );
  },
});

export default connect(
  (state) => ({
    emailTemplate:         EmailIntegrations.Selectors.getEmailTemplateDesigner(state).emailTemplate,
    isMessageDisplayed:    EmailIntegrations.Selectors.getEmailTemplateDesigner(state).isMessageDisplayed,
    isModalVisible:        EmailIntegrations.Selectors.getEmailTemplateDesigner(state).isModalVisible,
    tabIndex:              EmailIntegrations.Selectors.getEmailTemplateDesigner(state).tabIndex,
    previewEmail:          EmailIntegrations.Selectors.getEmailTemplateDesigner(state).preview.emailTo,
    isSendingTestEmail:    EmailIntegrations.Selectors.getEmailTemplateDesigner(state).preview.isSendingTestEmail,
    token:                 EmailIntegrations.Selectors.getEmailTemplateDesigner(state).token,
    showTokenWarning:      EmailIntegrations.Selectors.getEmailTemplateDesigner(state).showTokenWarning,
    showTestEmailMessage:  EmailIntegrations.Selectors.getEmailTemplateDesigner(state).showTestEmailMessage,
    showValidationMessage: EmailIntegrations.Selectors.showValidationMessage(state),
    formErrors:            EmailIntegrations.Selectors.formErrors(state),
  }),
  (dispatch) => ({
    closeValidatationMessage: () => {
      dispatch(EmailIntegrations.Actions.hideValidationMessage());
    },
    closeTestEmailMessage: () => {
      dispatch(EmailIntegrations.Actions.closeTestEmailMessage());
    },
    closeTokenMessage: () => {
      dispatch(EmailIntegrations.Actions.closeTokenMessage());
    },
    tryCloseEmailTemplateDesigner: () => {
      dispatch(EmailIntegrations.Actions.tryCloseEmailTemplateDesigner());
    },
    updateEmailTemplateField: (emailTemplate) => {
      dispatch(EmailIntegrations.Actions.updateEmailTemplateField(emailTemplate));
    },
    //uses the API to update an individual email template
    updateEmailTemplate: (emailTemplate) => {
      dispatch(EmailIntegrations.Actions.updateEmailTemplate(emailTemplate));
    },
    changeDesignerTab: (e, { value }) => {
      dispatch(EmailIntegrations.Actions.changeDesignerTab(value));
    },
    hideDesignerMessage: () => {
      dispatch(EmailIntegrations.Actions.hideDesignerMessage());
    },
    closeEmailTemplateDesigner: () => {
      dispatch(EmailIntegrations.Actions.closeEmailTemplateDesigner());
    },
    setPreviewEmail: (e, { value }) => {
      dispatch(EmailIntegrations.Actions.setPreviewEmail(value));
    },
    checkEmailTemplateTokens: (emailTemplate) => {
      dispatch(EmailIntegrations.Actions.checkEmailTemplateTokens(emailTemplate));
    },
    fetchCampaignTokenList: () => {
      dispatch(EmailIntegrations.Actions.fetchCampaignTokenList());
    },
    sendPreviewEmailTemplate: (emailTo, emailTemplate) => {
      dispatch(EmailIntegrations.Actions.sendPreviewEmailTemplate(emailTo, emailTemplate));
    },
  })
)(EmailDesigner);
